import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

// All checkout state + derived pricing + the Stripe redirect, extracted from CheckoutDialog
// so the component stays presentational.
export function useCheckout({ open, mode = "credits", planId = null, initialInterval = "month" }) {
  const [opts, setOpts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [selectedPacks, setSelectedPacks] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(planId);
  const [billingInterval, setBillingInterval] = useState(initialInterval);

  useEffect(() => {
    if (!open) return;
    setSelectedPacks([]);
    setSelectedPlan(planId);
    setBillingInterval(initialInterval);
    setLoading(true);
    api.get("/checkout/options").then((r) => setOpts(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [open, planId, initialInterval]);

  const isSub = mode === "subscription";
  const isNew = !!opts?.is_new_subscriber;
  // bonus pricing applies when subscribing AND brand-new
  const useBonus = (isSub && isNew) || (mode === "credits" && !!selectedPlan && isNew);
  const packs = useBonus ? (opts?.credit_packages_bonus || []) : (opts?.credit_packages_regular || []);
  const plan = (opts?.plans || []).find((p) => p.id === (isSub ? planId : selectedPlan));
  const yearly = billingInterval === "year";

  const togglePack = (id) =>
    setSelectedPacks((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const chosenPacks = packs.filter((p) => selectedPacks.includes(p.id));
  const welcomeBonus = (plan && isNew) ? (opts?.welcome_bonus_credits || 0) : 0;

  const planPrice = plan ? (yearly ? plan.price_yearly : plan.price) : 0;
  const planCredits = plan ? (yearly ? (plan.credits_yearly ?? plan.credits * 12) : plan.credits) : 0;

  // Video Create-a-thon: 20% off the first month of Starter (monthly billing only)
  const cat = opts?.createathon;
  const createathonApplies = !!(cat?.eligible && plan && plan.id === cat.plan_id && !yearly);
  const createathonDiscount = createathonApplies ? Math.round((plan.price - cat.sale_price) * 100) / 100 : 0;

  const total = useMemo(() => {
    const packPrice = chosenPacks.reduce((s, p) => s + p.price, 0);
    return Math.round((planPrice + packPrice - createathonDiscount) * 100) / 100;
  }, [planPrice, chosenPacks, createathonDiscount]);

  const totalCredits = planCredits + chosenPacks.reduce((s, p) => s + p.credits, 0) + welcomeBonus;

  const proceed = async () => {
    if (!plan && chosenPacks.length === 0) {
      toast.error("Select a plan or at least one credit pack");
      return;
    }
    setRedirecting(true);
    try {
      const { data } = await api.post("/checkout/create", {
        plan_id: isSub ? planId : (selectedPlan || null),
        credit_package_ids: selectedPacks,
        billing_interval: billingInterval,
        origin_url: window.location.origin,
        createathon_expected: createathonApplies,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(apiError(e));
      setRedirecting(false);
      // refresh server-side pricing (e.g. Create-a-thon just ended) so the dialog shows the real total
      api.get("/checkout/options").then((r) => setOpts(r.data)).catch(() => {});
    }
  };

  return {
    opts, loading, redirecting, selectedPacks, selectedPlan, setSelectedPlan,
    billingInterval, setBillingInterval, yearly, yearlyDiscountPct: opts?.yearly_discount_pct || 15,
    isSub, isNew, useBonus, packs, plan, planPrice, planCredits, togglePack, chosenPacks, welcomeBonus,
    createathonApplies, createathonDiscount, createathonSale: cat?.sale_price || 0,
    total, totalCredits, proceed,
  };
}
